import React from "react";
import {
    Container,
    Box,
    Typography,
    Divider,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Alert,
    Avatar
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCircleInfo,
    faGavel,
    faTriangleExclamation,
    faCheck,
    faLeaf,
    faBalanceScale,
    faUsers,
    faQuestionCircle,
    faCircle
} from "@fortawesome/free-solid-svg-icons";
// @ts-ignore
import avatarPresident from "../../assets/images/others/jpg.png";
import SectionTitle from "../../components/layouts/others/title-section.layout";

const POST20240824 = () => (
    <Container sx={{ mb: 5 }}>
        <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS" />

        <Box textAlign="center" mt={3} mb={3}>
            <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
                Remarques sur le PLUi décidé par le Maire
            </Typography>
            <Divider sx={{ mb: 2, width: '70%', mx: 'auto' }}/>
        </Box>

        <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, bgcolor: "#f8fafc", borderRadius: 2, mb: 3 }}>
            <Typography variant="body1" paragraph>
                Sur un sujet aussi essentiel pour l'avenir de la commune, les habitants n'ont pas été associés à l’élaboration de ce projet de PLUi, comme ils auraient dû l'être. Il aura fallu attendre la réunion publique du 15 juin 2024 pour découvrir le contenu du PLUi qui avait été décidé pour Bois-le-Roi, soit deux semaines seulement avant son premier arrêt au conseil d'agglomération, le 27 juin. Et encore, ce n'était qu'un aperçu du projet ; l'ensemble du dossier ne devait pas être communiqué aux habitants avant le conseil d'agglomération.
            </Typography>
            <Typography variant="body1" paragraph>
                A l'examen du contenu du projet de PLUi pour Bois-le-Roi, nous comprenons que le Maire ait attendu le dernier moment pour le dévoiler aux Bacots, en leur laissant croire qu'il serait plus protecteur.
            </Typography>

            <Alert
                severity="warning"
                sx={{ my: 2, fontWeight: 500 }}
                icon={<FontAwesomeIcon icon={faCircleInfo} />}
            >
                Le Maire a défini <b>23 secteurs de Mixité Sociale</b> sur lesquels il sera imposé pour toute construction, <b>80%* de logements locatifs sociaux (LLS)</b> dès le premier logement construit. <em>*avec arrondi à l’unité supérieure</em>
            </Alert>

            <Paper elevation={0} sx={{ p: 2, mb: 2, background: "#f4f9f9" }}>
                <Typography variant="subtitle2" color="text.secondary">
                    Le « secteur de mixité sociale » défini à l’article L151-15 du Code de l'Urbanisme est un périmètre au sein duquel tout programme d’habitat devra comprendre un pourcentage affecté à une catégorie de logements sociaux (LS) en vue d’atteindre des objectifs de mixité sociale. Cette servitude permet d'aider les communes et les EPCI à atteindre les objectifs de mixité sociale, en leur laissant le soin de définir le pourcentage à réserver aux LS.
                </Typography>
            </Paper>

            <Typography variant="body1" paragraph>
                Le Maire exploite ce dispositif dans sa version la plus coercitive possible, puisqu'il va jusqu'à interdire aux propriétaires de ces parcelles la possibilité de pouvoir y construire leurs propres maisons.
            </Typography>

            <Alert
                severity="error"
                sx={{ fontWeight: 600, my: 2 }}
                icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
            >
                Nous nous opposons à cette décision brutale qui ne correspond pas à nos valeurs !
                <br />
                Les secteurs de mixité sociale tels qu'ils ont été définis par le Maire sont une atteinte inacceptable au droit de la propriété.
            </Alert>

            <Typography variant="body1" paragraph>
                Qui voudrait racheter une parcelle sachant qu'il n'est pas possible d'y faire construire sa maison ? Personne, ou alors au prix du non constructible. Nous sommes solidaires des propriétaires spoliés.
                <br />
                Ce sont des projets de vie anéantis par une décision inutile prise par un Maire.
            </Typography>

            <Paper elevation={1} sx={{ p: 2, mb: 2, background: "#f2f7fa" }}>
                <Typography variant="body2">
                    En plus des deux secteurs à construire du PLH (Focel et gare), de l'OAP des Sesçois et d'une réglementation favorable à la densification, le Maire a intégré au PLUi une règle qui s’appliquera sur l'ensemble de la commune pour les logements collectifs à construire :
                </Typography>
                <Box sx={{ pl: 2 }}>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faCircle} size="xs" style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText primary={<Typography>3 à 15 logements envisagés = <b>80%* de LLS obligatoire</b></Typography>} />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faCircle} size="xs" style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText primary={<Typography>Plus de 16 logements = <b>75%* de LLS obligatoire</b></Typography>} />
                        </ListItem>
                    </List>
                </Box>
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    Autant de mesures qui démontrent que pour aller dans le sens de la loi SRU, le Maire n'avait pas besoin de spolier des propriétaires.
                </Typography>
            </Paper>

            <Alert
                severity="info"
                sx={{ my: 2, fontWeight: 480 }}
                icon={<FontAwesomeIcon icon={faGavel} />}
            >
                Nous demandons au Maire de modifier la réglementation de ses secteurs de mixité sociale pour permettre a minima aux propriétaires concernés de pouvoir y construire leurs propres habitations.
            </Alert>

            <Typography variant="body1" paragraph>
                Et d'autres points suscitent de l'incompréhension et des inquiétudes légitimes, tels la parcelle du Bois de la Source (OAP des Sesçois) et les zones réservées sur des parcelles boisées, condamnées à être bétonnées.
            </Typography>

            <Alert
                sx={{ my: 2, bgcolor: "#f7fbe7", color: "#50862b", border: "1px solid #c9e8a5" }}
                icon={<FontAwesomeIcon icon={faLeaf} color="#75cd36" />}
            >
                <b>Au-delà de cette modification prioritaire pour préserver notre commune d'une forte densification et mieux protéger son patrimoine naturel, nous demandons :</b>
            </Alert>
            <Box sx={{ pl: 1 }}>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faCheck} style={{ color: "#388e3c" }} />
                        </ListItemIcon>
                        <ListItemText primary="Pour les zones Uba et Ubb, que la largeur des bandes constructibles soit établie à 30 mètres, et que la limite de recul d’implantation du bâti par rapport aux voies et emprises publiques soit fixée à 6 mètres." />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faCheck} style={{ color: "#388e3c" }} />
                        </ListItemIcon>
                        <ListItemText primary="De réduire à une trentaine le nombre de logements à construire sur le site de la Focel." />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faCheck} style={{ color: "#388e3c" }} />
                        </ListItemIcon>
                        <ListItemText primary="De prendre en compte et de compléter la liste des corridors écologiques suivant la proposition de l'association TPAMPB." />
                    </ListItem>
                </List>
            </Box>

            <Box my={3}>
                <Alert icon={<FontAwesomeIcon icon={faQuestionCircle} />}>
                    Pourquoi le Maire n'a-t-il pas entamé des démarches pour que Bois-le-Roi obtienne le label Sites Patrimoniaux Remarquables (SPR), comme Barbizon ?
                </Alert>
            </Box>

            <Stack direction="row" alignItems="center" spacing={2} justifyContent="flex-end" mt={4}>
                <Box>
                    <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="primary"
                        letterSpacing={1}
                    >
                        Jean-Philippe GUIBERT
                    </Typography>
                    <Typography
                        variant="body2"
                        fontStyle="italic"
                        color="text.secondary"
                    >
                        Président
                    </Typography>
                </Box>
                <Avatar
                    alt="Photo de Jean-Philippe GUIBERT"
                    src={avatarPresident}
                    sx={{ width: 90, height: 90, boxShadow: 2, border: '2px solid #ddd' }}
                />
            </Stack>

            <Divider sx={{ my: 4 }} />

            <Box textAlign="center">
                <Typography variant="body2" color="textSecondary">
                    Le PLUi de Bois-le-Roi sera arrêté au Conseil Municipal du <b>jeudi 19 septembre 2024 à 20h</b>
                </Typography>
            </Box>
        </Paper>
    </Container>
);

export default POST20240824;